import { api_json } from './api'

export type WorkspaceTabSnapshot = {
  path: string
  title?: string
}

export type WorkspaceTabsSnapshot = {
  tabs: WorkspaceTabSnapshot[]
  active_path: string
}

const STORAGE_PREFIX = 'nr01.workspace.tabs.'
const PERSIST_DEBOUNCE_MS = 400

function safe_local_storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

function storage_key(user_id: string): string {
  return `${STORAGE_PREFIX}${user_id}`
}

function normalize_path(value: unknown): string | null {
  const raw = String(value || '').trim()
  if (!raw) return null
  const path = raw.split('?')[0].split('#')[0]
  if (!path.startsWith('/')) return null
  return path
}

export function sanitize_workspace_snapshot(
  value: unknown,
  allowed: Set<string>
): WorkspaceTabsSnapshot | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Partial<WorkspaceTabsSnapshot>
  const raw_tabs = Array.isArray(raw.tabs) ? raw.tabs : []
  const seen = new Set<string>()
  const tabs: WorkspaceTabSnapshot[] = []

  for (const entry of raw_tabs) {
    const path = typeof entry === 'string'
      ? normalize_path(entry)
      : normalize_path(entry && typeof entry === 'object' ? (entry as { path?: unknown }).path : null)
    if (!path || !allowed.has(path) || seen.has(path)) continue
    seen.add(path)
    const title = entry && typeof entry === 'object'
      ? String((entry as { title?: unknown }).title || '').trim()
      : ''
    tabs.push(title ? { path, title: title.slice(0, 80) } : { path })
    if (tabs.length >= 12) break
  }

  if (!tabs.length) return null

  const active_candidate = normalize_path(raw.active_path)
  const active_path = active_candidate && seen.has(active_candidate)
    ? active_candidate
    : tabs[0].path

  return { tabs, active_path }
}

export function load_workspace_tabs_local(
  user_id: string,
  allowed: Set<string>
): WorkspaceTabsSnapshot | null {
  const storage = safe_local_storage()
  if (!storage) return null
  try {
    const raw = storage.getItem(storage_key(user_id))
    if (!raw) return null
    return sanitize_workspace_snapshot(JSON.parse(raw), allowed)
  } catch {
    return null
  }
}

export function save_workspace_tabs_local(user_id: string, snapshot: WorkspaceTabsSnapshot): void {
  const storage = safe_local_storage()
  if (!storage) return
  try {
    storage.setItem(storage_key(user_id), JSON.stringify(snapshot))
  } catch {
    // quota / private mode
  }
}

export function clear_workspace_tabs_local(user_id: string): void {
  const storage = safe_local_storage()
  if (!storage) return
  try {
    storage.removeItem(storage_key(user_id))
  } catch {
    // ignore
  }
}

export async function load_workspace_tabs_remote(
  allowed: Set<string>
): Promise<WorkspaceTabsSnapshot | null> {
  try {
    const data = await api_json<WorkspaceTabsSnapshot>('/api/auth/workspace-tabs-preferences')
    return sanitize_workspace_snapshot(data, allowed)
  } catch {
    return null
  }
}

export async function save_workspace_tabs_remote(snapshot: WorkspaceTabsSnapshot): Promise<void> {
  await api_json('/api/auth/workspace-tabs-preferences', {
    method: 'POST',
    body: JSON.stringify(snapshot)
  })
}

type PersistController = {
  user_id: string
  timer: ReturnType<typeof setTimeout> | null
  seq: number
}

let controller: PersistController | null = null

export function schedule_persist_workspace_tabs(
  user_id: string,
  snapshot: WorkspaceTabsSnapshot
): void {
  save_workspace_tabs_local(user_id, snapshot)

  if (!controller || controller.user_id !== user_id) {
    if (controller?.timer) clearTimeout(controller.timer)
    controller = { user_id, timer: null, seq: 0 }
  }

  const current = controller
  current.seq += 1
  const seq = current.seq

  if (current.timer) clearTimeout(current.timer)
  current.timer = setTimeout(() => {
    current.timer = null
    void (async () => {
      try {
        await save_workspace_tabs_remote(snapshot)
      } catch {
        // local já salvo; API best-effort
      }
      if (controller && controller.seq === seq) {
        // ok
      }
    })()
  }, PERSIST_DEBOUNCE_MS)
}

export function flush_persist_workspace_tabs(): void {
  if (controller?.timer) {
    clearTimeout(controller.timer)
    controller.timer = null
  }
}
